import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import { Popover, Transition } from "@headlessui/react";
import { use100vh } from "react-div-100vh";
import { PopupModal } from "react-calendly";

import {
  ChevronDownIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/solid";

import Modal from "@/components/blocks/contact/modal";
import LinkWrapper from "@/components/linkWrapper";
import MediaWrapper from "@/components/mediaWrapper";
import LanguageSwitcherMenu from "@/components/languages/language-switcher-menu";
import getLanguageMetaData from "@/components/languages/get-language-meta-data";

export const query = graphql`
  fragment NavbarCenteredWithBottomBorderData on StrapiComponentNavbarCenteredWithBottomBorder {
    companyLogo {
      ...StrapiSharedLink
    }
    companyLogoWhite {
      ...StrapiSharedLink
    }
    openMenuText
    closeMenuText

    menu1SingleEntry {
      ...StrapiSharedLink
    }
    menu2Title
    menu2Links {
      ...StrapiSharedLink
    }
    menu2CallToActions {
      ...StrapiSharedLink
    }
    menu3SingleEntry {
      ...StrapiSharedLink
    }
    menu4Title
    menu4Links {
      ...StrapiSharedLink
    }
    menu4CallToActions {
      ...StrapiSharedLink
    }

    infobarLinks {
      strapiName
      ...StrapiSharedLink
    }
    infobarModalContactFormCloseText
    infobarModalContactForm {
      ...ContactCenteredData
    }
    infobarModalArrangeMeetingUrl

    buttonArrangeDemo {
      ...StrapiSharedLink
    }
    buttonLogin {
      ...StrapiSharedLink
    }
    buttonLoginMobileText
  }
`;

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const isObject = (o) => {
  return o && typeof o === "object" && !Array.isArray(o);
};

function CenteredWithBottomBorder({
  data,
  locale,
  languageLinks = {},
  switchBackground = true,
}) {
  if (!data || !locale) {
    return <></>;
  }

  const mobileMenu3 = [
    data.menu3SingleEntry,
    ...(data.menu4Links || []),
  ].filter(Boolean);

  const languageLinksEntries =
    (isObject(languageLinks) && Object.entries(languageLinks)) || null;

  const screenHeight = use100vh();

  const [dialogContactFormOpen, setDialogContactFormOpen] = useState(false);
  const [dialogArrangeMeetingOpen, setDialogArrangeMeetingOpen] =
    useState(false);
  const [isTop, setIsTop] = useState(true);

  useEffect(() => {
    const scrolled = typeof window !== "undefined" && window.scrollY < 20;
    if (scrolled !== isTop) {
      setIsTop(scrolled);
    }

    if (typeof document !== "undefined") {
      document.addEventListener(
        "scroll",
        () => {
          const scrolled = typeof window !== "undefined" && window.scrollY < 20;
          if (scrolled !== isTop) {
            setIsTop(scrolled);
          }
        },
        { passive: true }
      );
    }
  });

  return (
    <div id="modal-root">
      <Modal
        open={dialogContactFormOpen}
        setOpen={setDialogContactFormOpen}
        classNameBackground="bg-background-primary"
        srTextClose={data.infobarModalContactFormCloseText}
        data={data.infobarModalContactForm}
        formVersion={data.infobarModalContactForm?.version}
      />
      {typeof document !== "undefined" &&
        data.infobarModalArrangeMeetingUrl && (
          <PopupModal
            url={data.infobarModalArrangeMeetingUrl}
            rootElement={document.getElementById("modal-root")}
            open={dialogArrangeMeetingOpen}
            onModalClose={() => setDialogArrangeMeetingOpen(false)}
          />
        )}
      {!switchBackground && (
        <div className="h-24 shrink-0 grow-0 bg-background-white" />
      )}
      <Popover
        className={classNames(
          "fixed inset-x-0 top-0 z-[100]",
          switchBackground && isTop
            ? "bg-background-transparent"
            : "bg-background-white shadow-md"
        )}
      >
        {!isTop && Boolean(data.infobarLinks?.length) && (
          <div className="w-full bg-background-primary text-text-on-primary">
            <div className="-mx-2 flex max-w-7xl justify-end gap-8 px-4 sm:px-6 lg:mx-auto">
              {Boolean(data.infobarLinks?.length) &&
                data.infobarLinks.map(
                  (item, index) =>
                    (item.overwriteTitle || item.link?.title) &&
                    (item.strapiName?.toLowerCase() === "modal contact form" ? (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setDialogContactFormOpen(true)}
                        className={classNames(
                          "filter-color-on-primary flex items-center justify-center gap-1 px-2 py-2 hover:text-text-on-primary-light lg:p-0"
                        )}
                      >
                        {(item.overwriteIcon || item.link?.icon) && (
                          <MediaWrapper
                            media={item.overwriteIcon || item.link?.icon}
                            objectFit="contain"
                            divClassName="h-7 w-7 flex-shrink-0 lg:h-4 lg:w-4"
                            aria-hidden="true"
                          />
                        )}
                        <div
                          className={classNames(
                            "sr-only ml-1",
                            (item.overwriteIcon || item.link?.icon) &&
                              "lg:not-sr-only"
                          )}
                        >
                          <span className="font-[Poppins,sans-serif] text-base font-normal">
                            {item.overwriteTitle || item.link?.title}
                          </span>
                        </div>
                      </button>
                    ) : item.strapiName?.toLowerCase() ===
                      "modal arrange meeting" ? (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setDialogArrangeMeetingOpen(true)}
                        className={classNames(
                          "filter-color-on-primary flex items-center justify-center gap-1 px-2 py-2 hover:text-text-on-primary-light lg:p-0"
                        )}
                      >
                        {(item.overwriteIcon || item.link?.icon) && (
                          <MediaWrapper
                            media={item.overwriteIcon || item.link?.icon}
                            objectFit="contain"
                            divClassName="h-7 w-7 flex-shrink-0 lg:h-4 lg:w-4"
                            aria-hidden="true"
                          />
                        )}
                        <div
                          className={classNames(
                            "sr-only ml-1",
                            (item.overwriteIcon || item.link?.icon) &&
                              "lg:not-sr-only"
                          )}
                        >
                          <span className="font-[Poppins,sans-serif] text-base font-normal">
                            {item.overwriteTitle || item.link?.title}
                          </span>
                        </div>
                      </button>
                    ) : (
                      <LinkWrapper
                        key={index}
                        to={item.link?.url}
                        className={classNames(
                          "flex items-center justify-center gap-1 px-2 py-2 hover:text-text-on-primary-light lg:p-0"
                        )}
                      >
                        {(item.overwriteIcon || item.link?.icon) && (
                          <MediaWrapper
                            media={item.overwriteIcon || item.link?.icon}
                            objectFit="contain"
                            divClassName="h-7 w-7 flex-shrink-0 lg:h-4 lg:w-4"
                            aria-hidden="true"
                          />
                        )}
                        <div
                          className={classNames(
                            "sr-only ml-1",
                            (item.overwriteIcon || item.link?.icon) &&
                              "lg:not-sr-only"
                          )}
                        >
                          <span className="font-[Poppins,sans-serif] text-base font-normal">
                            {item.overwriteTitle || item.link?.title}
                          </span>
                        </div>
                      </LinkWrapper>
                    ))
                )}
            </div>
          </div>
        )}
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center">
              {(data.companyLogoWhite?.overwriteIcon ||
                data.companyLogoWhite?.link?.icon) &&
                (data.companyLogo?.overwriteIcon ||
                  data.companyLogo?.link?.icon) && (
                  <div className="mr-8 flex-none">
                    <LinkWrapper to={data.companyLogoWhite?.link?.url || "/"}>
                      {(data.companyLogoWhite?.overwriteTitle ||
                        data.companyLogoWhite?.link?.title) && (
                        <span className="sr-only">
                          {data.companyLogoWhite.overwriteTitle ||
                            data.companyLogoWhite.link.title}
                        </span>
                      )}
                      <MediaWrapper
                        divClassName="h-8"
                        width="auto"
                        media={
                          switchBackground && isTop
                            ? data.companyLogoWhite.overwriteIcon ||
                              data.companyLogoWhite.link.icon
                            : data.companyLogo.overwriteIcon ||
                              data.companyLogo.link.icon
                        }
                        objectFit="contain"
                      />
                    </LinkWrapper>
                  </div>
                )}
              {(Boolean(data.menu1SingleEntry) ||
                (data.menu2Title && Boolean(data.menu2Links?.length)) ||
                Boolean(data.menu3SingleEntry) ||
                (data.menu4itle && Boolean(data.menu4Links?.length))) && (
                <Popover.Group
                  as="nav"
                  className="hidden space-x-4 lg:flex lg:flex-1 lg:items-center xl:space-x-8"
                >
                  {Boolean(data.menu1SingleEntry) &&
                    (data.menu1SingleEntry?.overwriteTitle ||
                      data.menu1SingleEntry?.link?.title) && (
                      <LinkWrapper
                        to={data.menu1SingleEntry?.link?.url}
                        className={classNames(
                          "text-base font-medium",
                          switchBackground && isTop
                            ? "text-text-on-primary hover:text-text-on-primary-light"
                            : "text-text-primary hover:text-text-primary-light"
                        )}
                      >
                        <span className="font-sansBold">
                          {data.menu1SingleEntry?.overwriteTitle ||
                            data.menu1SingleEntry?.link?.title}
                        </span>
                      </LinkWrapper>
                    )}
                  {data.menu2Title && Boolean(data.menu2Links?.length) && (
                    <Popover className="relative">
                      {({ open }) => {
                        return (
                          <>
                            <Popover.Button
                              className={classNames(
                                "group inline-flex items-center rounded-md bg-background-transparent text-base font-medium focus:outline-none",
                                switchBackground && isTop
                                  ? classNames(
                                      open
                                        ? "text-text-on-primary-light"
                                        : "text-text-on-primary",
                                      "hover:text-text-on-primary-light"
                                    )
                                  : classNames(
                                      open
                                        ? "text-text-primary-light"
                                        : "text-text-primary",
                                      "hover:text-text-primary-light"
                                    )
                              )}
                            >
                              <span>{data.menu2Title}</span>
                              <ChevronDownIcon
                                className="ml-2 h-5 w-5 shrink-0"
                                aria-hidden="true"
                              />
                            </Popover.Button>

                            <Transition
                              as={Fragment}
                              enter="transition ease-out duration-200"
                              enterFrom="opacity-0 translate-y-1"
                              enterTo="opacity-100 translate-y-0"
                              leave="transition ease-in duration-150"
                              leaveFrom="opacity-100 translate-y-0"
                              leaveTo="opacity-0 translate-y-1"
                            >
                              <Popover.Panel className="absolute z-10 -ml-4 mt-3 w-screen max-w-xl transform px-2 sm:px-0 lg:left-1/2 lg:ml-0 lg:-translate-x-1/2">
                                <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-ring-black ring-opacity-5">
                                  <div className="relative grid gap-6 bg-background-white px-5 py-6 sm:gap-8 sm:p-8">
                                    {data.menu2Links.map(
                                      (item, index) =>
                                        (item.overwriteTitle ||
                                          item.link?.title) && (
                                          <LinkWrapper
                                            key={index}
                                            to={item.link?.url}
                                            className="-m-3 flex items-start rounded-lg p-3 hover:bg-background-off-white"
                                          >
                                            <div className="w-20 shrink-0 ">
                                              {(item.overwriteIcon ||
                                                item.link?.icon) && (
                                                <MediaWrapper
                                                  media={
                                                    item.overwriteIcon ||
                                                    item.link?.icon
                                                  }
                                                  objectFit="contain"
                                                  divClassName="w-full h-20 text-text-primary"
                                                  aria-hidden="true"
                                                />
                                              )}
                                            </div>
                                            <div className="ml-4">
                                              <p className="text-base font-medium text-text-primary">
                                                {item.overwriteTitle ||
                                                  item.link?.title}
                                              </p>
                                              {(item.overwriteDescription ||
                                                item.link?.description) && (
                                                <p className="mt-1 text-sm text-text-secondary">
                                                  {item.overwriteDescription ||
                                                    item.link?.description}
                                                </p>
                                              )}
                                            </div>
                                          </LinkWrapper>
                                        )
                                    )}
                                  </div>
                                  {Boolean(data.menu2CallToActions?.length) && (
                                    <div className="space-y-6 bg-background-off-white px-5 py-5 sm:flex sm:space-x-10 sm:space-y-0 sm:px-8">
                                      {data.menu2CallToActions.map(
                                        (button, index) => (
                                          <div
                                            key={index}
                                            className="flow-root"
                                          >
                                            {(button.overwriteTitle ||
                                              button.link?.title) && (
                                              <LinkWrapper
                                                to={button.link?.url}
                                                className={classNames(
                                                  "-m-3 flex items-center rounded-md p-3 text-base font-medium text-text-primary",
                                                  button.link?.url &&
                                                    "hover:bg-background-light-gray"
                                                )}
                                              >
                                                {(button.overwriteIcon ||
                                                  button.link?.icon) && (
                                                  <MediaWrapper
                                                    media={
                                                      button.overwriteIcon ||
                                                      button.link.icon
                                                    }
                                                    objectFit="contain"
                                                    divClassName="flex-shrink-0 h-6 w-6 text-text-secondary"
                                                    aria-hidden="true"
                                                  />
                                                )}
                                                <span className="ml-3">
                                                  {button.overwriteTitle ||
                                                    button.link.title}
                                                </span>
                                              </LinkWrapper>
                                            )}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  )}
                                </div>
                              </Popover.Panel>
                            </Transition>
                          </>
                        );
                      }}
                    </Popover>
                  )}
                  {Boolean(data.menu3SingleEntry) &&
                    (data.menu3SingleEntry?.overwriteTitle ||
                      data.menu3SingleEntry?.link?.title) && (
                      <LinkWrapper
                        to={data.menu3SingleEntry?.link?.url}
                        className={classNames(
                          "text-base font-medium",
                          switchBackground && isTop
                            ? "text-text-on-primary hover:text-text-on-primary-light"
                            : "text-text-primary hover:text-text-primary-light"
                        )}
                      >
                        <span className="font-sansBold">
                          {data.menu3SingleEntry?.overwriteTitle ||
                            data.menu3SingleEntry?.link?.title}
                        </span>
                      </LinkWrapper>
                    )}
                  {data.menu4Title && Boolean(data.menu4Links?.length) && (
                    <Popover className="relative">
                      {({ open }) => {
                        return (
                          <>
                            <Popover.Button
                              className={classNames(
                                "group inline-flex items-center rounded-md bg-background-transparent text-base font-medium focus:outline-none",
                                switchBackground && isTop
                                  ? classNames(
                                      open
                                        ? "text-text-on-primary-light"
                                        : "text-text-on-primary",
                                      "hover:text-text-on-primary-light"
                                    )
                                  : classNames(
                                      open
                                        ? "text-text-primary-light"
                                        : "text-text-primary",
                                      "hover:text-text-primary-light"
                                    )
                              )}
                            >
                              <span>{data.menu4Title}</span>
                              <ChevronDownIcon
                                className="ml-2 h-5 w-5 shrink-0"
                                aria-hidden="true"
                              />
                            </Popover.Button>

                            <Transition
                              as={Fragment}
                              enter="transition ease-out duration-200"
                              enterFrom="opacity-0 translate-y-1"
                              enterTo="opacity-100 translate-y-0"
                              leave="transition ease-in duration-150"
                              leaveFrom="opacity-100 translate-y-0"
                              leaveTo="opacity-0 translate-y-1"
                            >
                              <Popover.Panel className="absolute left-1/2 z-10 mt-3 w-screen max-w-xs -translate-x-1/2 transform px-2 sm:px-0">
                                <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                                  <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8">
                                    {data.menu4Links.map(
                                      (item, index) =>
                                        (item.overwriteTitle ||
                                          item.link?.title) && (
                                          <LinkWrapper
                                            key={index}
                                            to={item.link?.url}
                                            className="-m-3 block rounded-md p-3 hover:bg-background-off-white"
                                          >
                                            <p className="text-base font-medium text-text-primary">
                                              {item.overwriteTitle ||
                                                item.link?.title}
                                            </p>
                                          </LinkWrapper>
                                        )
                                    )}
                                  </div>
                                </div>
                              </Popover.Panel>
                            </Transition>
                          </>
                        );
                      }}
                    </Popover>
                  )}
                </Popover.Group>
              )}
            </div>
            <div className="-my-2 -mr-2 lg:hidden">
              <Popover.Button
                className={classNames(
                  "inline-flex items-center justify-center rounded-md bg-background-transparent p-2 focus:outline-none",
                  switchBackground && isTop
                    ? "text-text-on-primary hover:text-text-on-primary-light"
                    : "text-text-primary hover:text-text-primary-light"
                )}
              >
                {data.openMenuText && (
                  <span className="sr-only">{data.openMenuText}</span>
                )}
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </Popover.Button>
            </div>
            <div className="hidden flex-none items-center space-x-4 lg:flex xl:space-x-8">
              <LanguageSwitcherMenu
                className={classNames(
                  "",
                  switchBackground && isTop
                    ? "text-text-on-primary hover:text-text-on-primary-light"
                    : "text-text-primary hover:text-text-primary-light"
                )}
                currentLanguage={locale}
                languageLinks={languageLinks}
                showFlag
              />
              {(data.buttonLogin?.overwriteTitle ||
                data.buttonLogin?.link?.title) && (
                <LinkWrapper
                  to={data.buttonLogin?.link?.url}
                  className={classNames(
                    "text-base font-medium",
                    switchBackground && isTop
                      ? "text-text-on-primary hover:text-text-on-primary-light"
                      : "text-text-primary hover:text-text-primary-light"
                  )}
                >
                  <span>
                    {data.buttonLogin?.overwriteTitle ||
                      data.buttonLogin?.link?.title}
                  </span>
                </LinkWrapper>
              )}
              {(data.buttonArrangeDemo?.overwriteTitle ||
                data.buttonArrangeDemo?.link?.title) && (
                <LinkWrapper
                  to={data.buttonArrangeDemo?.link?.url}
                  className="rounded-md bg-background-highlight px-4 py-2 text-base font-medium leading-6 text-white duration-150 ease-in-out hover:bg-background-highlight-dark hover:text-text-highlight-light focus:outline-none active:bg-background-highlight-dark"
                >
                  <span>
                    {data.buttonArrangeDemo?.overwriteTitle ||
                      data.buttonArrangeDemo?.link?.title}
                  </span>
                </LinkWrapper>
              )}
            </div>
          </div>
        </div>

        {/* mobile view */}
        <Transition
          as={Fragment}
          enter="duration-200 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Popover.Panel
            focus
            className="absolute inset-x-0 bottom-0 top-0 origin-top-right transform p-2 transition lg:hidden"
            style={{ zIndex: 1000 }} /* without z-index hidden behind hero */
            tabIndex={-1} /* without tab-index not clickable in Safari*/
          >
            <div
              className="divide-y-2 divide-divide-light overflow-auto overscroll-contain rounded-lg bg-background-white shadow-lg ring-1 ring-ring-black ring-opacity-5"
              style={{ maxHeight: screenHeight ? screenHeight - 16 : "92vh" }}
            >
              <div className="relative px-5 pb-6 pt-5">
                <div className="flex items-center justify-between">
                  <div>
                    <LinkWrapper to={data.companyLogo?.link?.url || "/"}>
                      {(data.companyLogo?.overwriteTitle ||
                        data.companyLogo?.link?.title) && (
                        <span className="sr-only">
                          {data.companyLogo.overwriteTitle ||
                            data.companyLogo.link.title}
                        </span>
                      )}
                      {(data.companyLogo?.overwriteIcon ||
                        data.companyLogo?.link?.icon) && (
                        <MediaWrapper
                          divClassName="h-8 w-auto"
                          media={
                            data.companyLogo.overwriteIcon ||
                            data.companyLogo.link.icon
                          }
                          objectFit="contain"
                        />
                      )}
                    </LinkWrapper>
                  </div>
                  <div className="-mr-2">
                    <Popover.Button className="inline-flex items-center justify-center rounded-md bg-background-transparent p-2 text-text-primary hover:bg-background-light-gray hover:text-text-primary-light focus:outline-none">
                      {data.closeMenuText && (
                        <span className="sr-only">{data.closeMenuText}</span>
                      )}
                      <XMarkIcon
                        className="h-6 w-6 xs:h-8 xs:w-8"
                        aria-hidden="true"
                      />
                    </Popover.Button>
                  </div>
                </div>
              </div>
              {Boolean(data.menu2Links?.length) && (
                <div className="space-y-6 px-5 py-6">
                  <div
                    className={classNames(
                      "grid grid-cols-2 gap-x-8 gap-y-4",
                      data.menu2Links?.length > 2 && "md:grid-cols-3"
                    )}
                  >
                    {data.menu2Links?.map(
                      (item, index) =>
                        (item.overwriteTitle || item.link?.title) && (
                          <LinkWrapper
                            key={index}
                            to={item.link?.url}
                            className="text-base font-medium text-text-primary hover:text-text-secondary-dark"
                          >
                            {item.overwriteTitle || item.link?.title}
                          </LinkWrapper>
                        )
                    )}
                  </div>
                </div>
              )}
              {Boolean(mobileMenu3.length) && (
                <div className="space-y-6 px-5 py-6">
                  <div
                    className={classNames(
                      "grid grid-cols-2 gap-x-8 gap-y-4",
                      mobileMenu3.length > 2 && "md:grid-cols-3"
                    )}
                  >
                    {mobileMenu3.map(
                      (item, index) =>
                        (item.overwriteTitle || item.link?.title) && (
                          <LinkWrapper
                            key={index}
                            to={item.link?.url}
                            className="text-base font-medium text-text-primary hover:text-text-secondary-dark"
                          >
                            {item.overwriteTitle || item.link?.title}
                          </LinkWrapper>
                        )
                    )}
                  </div>
                </div>
              )}
              <div className="flex flex-col space-y-8 px-4 py-8">
                {Boolean(languageLinksEntries?.length) && (
                  <div
                    className={classNames(
                      "grid grid-cols-2",
                      languageLinksEntries.length > 2 && "md:grid-cols-3"
                    )}
                  >
                    {languageLinksEntries
                      .sort()
                      .map(([language, url], index) => (
                        <LinkWrapper
                          key={index}
                          to={locale === language ? null : url}
                          className={classNames(
                            "group flex items-center rounded-md px-4 py-2 text-sm text-text-dark-gray hover:bg-background-light-gray",
                            locale === language
                              ? "bg-background-light-gray"
                              : ""
                          )}
                        >
                          <div className="flex-0 mr-3 w-5" aria-hidden="true">
                            {getLanguageMetaData(language).flag}
                          </div>
                          {getLanguageMetaData(language).nativeName}
                        </LinkWrapper>
                      ))}
                  </div>
                )}
                {(data.buttonArrangeDemo?.overwriteTitle ||
                  data.buttonArrangeDemo?.link?.title) && (
                  <LinkWrapper
                    to={data.buttonArrangeDemo?.link?.url}
                    className="rounded-md bg-background-highlight px-4 py-2 text-center text-base font-medium leading-6 text-white duration-150 ease-in-out hover:bg-background-highlight-dark hover:text-text-highlight-light focus:outline-none active:bg-background-highlight-dark"
                  >
                    {data.buttonArrangeDemo?.overwriteTitle ||
                      data.buttonArrangeDemo?.link?.title}
                  </LinkWrapper>
                )}
                {(data.buttonLogin?.overwriteTitle ||
                  data.buttonLogin?.link?.title) && (
                  <div className="w-full text-center">
                    {data.buttonLoginMobileText
                      ? data.buttonLoginMobileText
                      : ""}
                    <LinkWrapper
                      to={data.buttonLogin?.link?.url}
                      className="px-2 py-2 text-text-highlight hover:text-text-highlight-dark"
                    >
                      {data.buttonLogin?.overwriteTitle ||
                        data.buttonLogin?.link?.title}
                    </LinkWrapper>
                  </div>
                )}
              </div>
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>
    </div>
  );
}

CenteredWithBottomBorder.propTypes = {
  data: PropTypes.object,
  locale: PropTypes.string,
  languageLinks: PropTypes.object,
  switchBackground: PropTypes.bool,
};

export default CenteredWithBottomBorder;
